import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";

import { Layout } from "./features/Layout";
import { Home } from "./features/Home";
import { Auth, RequireAuth, RequireAdmin } from "./features/Auth";
import { Dashboard } from "./features/Dashboard";
import { CreateOrder } from "./features/CreateOrder";
import { CancelOrder } from "./features/CancelOrder";
import { Schedule, DriversView } from "./features/Drivers";
import { Orders } from "./features/Orders";
import { OrderDetails } from "./features/Orders/routes";
import { Schedules } from "./features/Schedules";
import { DumpYardsView, DUMP_YARD_TABS } from "./features/DumpYards";
import { ReceivedOrdersView } from "./features/ReceivedOrders";
import { ClientOrder, ClientOrderHistory } from "./features/ClientDashboard";
import { PunchCardsView } from "./features/PunchCards";
import {
  OwesReportView,
  HGEOwesReportView,
  SmartRoofOwesReportView,
  DailyReportView,
  DailyReportOrdersView,
  DailyReportDriverView,
  DailyReportDriverOrdersView,
  YearlyReportView,
  ProfitReportView,
  DailyStaxReportView,
} from "./features/Reports";
import { DumpstersView, DumpstersMapView, DumpsterDetails } from "./features/Dumpsters";
import { VehiclesView } from "./features/Vehicles";
import { DVIRManagementView } from "./features/DVIR";
import { Customers, CustomerDetail } from "./features/Customers";
import { ReceiptsView } from "./features/Receipts/ReceiptsView/ReceiptsView";
import { RunsList, Result, NewRunConfig } from "./features/FleetOptimization";
import Scorecard from "./features/FleetOptimization/Scorecard/Scorecard";
import { SettingsView, SETTINGS_TABS } from "./features/Settings";
import { PrivacyPolicy, TermsOfService } from "./features/Legal";
import { captureAttribution } from "./lib/attribution";

function App() {
  // Stash any ad-platform click ID from the landing URL before the user
  // navigates away, so it survives until order creation.
  React.useEffect(() => {
    captureAttribution(window.location.search);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="create-order" element={<CreateOrder />} />
            <Route path="cancel-order" element={<CancelOrder />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />
            <Route path="auth/*" element={<Auth />} />

            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />

            <Route
              path="/drivers/:id"
              element={
                <RequireAdmin>
                  <Schedule />
                </RequireAdmin>
              }
            />

            <Route
              path="/drivers"
              element={
                <RequireAdmin>
                  <DriversView />
                </RequireAdmin>
              }
            />

            <Route
              path="/customers"
              element={
                <RequireAdmin>
                  <Customers />
                </RequireAdmin>
              }
            />

            <Route
              path="/customers/:id"
              element={
                <RequireAdmin>
                  <CustomerDetail />
                </RequireAdmin>
              }
            />

            {/* <Route path="/orders/:id" element={<Order />} /> */}
            <Route path="/orders/:id" element={<OrderDetails />} />

            <Route
              path="/orders"
              element={
                <RequireAuth>
                  <Orders />
                </RequireAuth>
              }
            />

            <Route
              path="/received_orders"
              element={
                <RequireAuth>
                  <ReceivedOrdersView />
                </RequireAuth>
              }
            />

            <Route
              path="/schedules"
              element={
                <RequireAdmin>
                  <Schedules />
                </RequireAdmin>
              }
            />

            <Route
              path="/dump_yards"
              element={
                <RequireAdmin>
                  <DumpYardsView />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="dump_yard" replace />} />
              {DUMP_YARD_TABS.map((tab) => (
                <Route key={tab.id} path={tab.id} element={tab.element} />
              ))}
            </Route>

            <Route
              path="/customer_orders/history"
              element={<ClientOrderHistory />}
            />

            <Route path="/customer_orders/:id" element={<ClientOrder />} />

            <Route
              path="/punch_cards"
              element={
                <RequireAdmin>
                  <PunchCardsView />
                </RequireAdmin>
              }
            />

            <Route
              path="/reports/transactions"
              element={
                <RequireAdmin>
                  <DailyStaxReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/owes"
              element={
                <RequireAdmin>
                  <OwesReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/hge-owes"
              element={
                <RequireAdmin>
                  <HGEOwesReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/smartroof-owes"
              element={
                <RequireAdmin>
                  <SmartRoofOwesReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/daily"
              element={
                <RequireAdmin>
                  <DailyReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/daily-orders"
              element={
                <RequireAdmin>
                  <DailyReportOrdersView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/daily-driver"
              element={
                <RequireAdmin>
                  <DailyReportDriverView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/daily-driver-orders"
              element={
                <RequireAdmin>
                  <DailyReportDriverOrdersView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/yearly"
              element={
                <RequireAdmin>
                  <YearlyReportView />
                </RequireAdmin>
              }
            />
            <Route
              path="/reports/profit"
              element={
                <RequireAdmin>
                  <ProfitReportView />
                </RequireAdmin>
              }
            />

            <Route
              path="/dumpsters"
              element={
                <RequireAdmin>
                  <DumpstersView />
                </RequireAdmin>
              }
            />

            <Route
              path="/dumpsters/map"
              element={
                <RequireAdmin>
                  <DumpstersMapView />
                </RequireAdmin>
              }
            />

            <Route
              path="/dumpsters/:id"
              element={
                <RequireAdmin>
                  <DumpsterDetails />
                </RequireAdmin>
              }
            />

            <Route
              path="/vehicles"
              element={
                <RequireAdmin>
                  <VehiclesView />
                </RequireAdmin>
              }
            />

            <Route
              path="/dvir"
              element={
                <RequireAdmin>
                  <DVIRManagementView />
                </RequireAdmin>
              }
            />

            <Route
              path="/receipts"
              element={
                <RequireAdmin>
                  <ReceiptsView />
                </RequireAdmin>
              }
            />

            <Route
              path="/fleet-optimization"
              element={
                <RequireAdmin>
                  <RunsList />
                </RequireAdmin>
              }
            />

            <Route
              path="/fleet-optimization/new"
              element={
                <RequireAdmin>
                  <NewRunConfig />
                </RequireAdmin>
              }
            />

            <Route
              path="/fleet-optimization/:id"
              element={
                <RequireAdmin>
                  <Result />
                </RequireAdmin>
              }
            />

            <Route
              path="/fleet-optimization/:id/scorecard"
              element={
                <RequireAdmin>
                  <Scorecard />
                </RequireAdmin>
              }
            />

            <Route
              path="/settings"
              element={
                <RequireAdmin>
                  <SettingsView />
                </RequireAdmin>
              }
            >
              <Route index element={<Navigate to="api-keys" replace />} />
              {SETTINGS_TABS.map((tab) => (
                <Route key={tab.path} path={tab.path} element={tab.element} />
              ))}
            </Route>

            <Route
              path="*"
              element={
                <main style={{ padding: "1rem" }}>
                  <p>There's nothing here!</p>
                </main>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
